import { createBox, useResponsiveProp } from '@shopify/restyle';
import type { ComponentProps, ComponentType, ReactNode } from 'react';
import { Children, Fragment } from 'react';
import Animated from 'react-native-reanimated';

import { isMobileNative } from 'app/utils/web-helpers';
import type { Theme } from 'app/shopify';

const BaseBox = createBox<Theme>();

interface BoxProps extends ComponentProps<typeof BaseBox> {
  gap?: ComponentProps<typeof BaseBox>['marginTop'];
  separator?: ReactNode;
  innerRef?: ComponentProps<typeof BaseBox>['ref'];
}

export function Box({ children, gap, separator, innerRef, ...rest }: BoxProps) {
  const resolvedGap = useResponsiveProp(gap);

  const sizeProperty = rest.flexDirection === 'row' ? ('marginEnd' as const) : ('marginTop' as const);

  const filteredChildren = Children.toArray(children).filter(
    (child): child is Exclude<typeof child, string | number> => typeof child !== 'string' && typeof child !== 'number',
  );

  return (
    <BaseBox {...rest} ref={innerRef}>
      {gap === undefined && separator === undefined
        ? filteredChildren
        : filteredChildren.map((child, index, array) => (
            <Fragment key={(child as { key: string | null }).key}>
              {child}

              {index !== array.length - 1 && separator !== undefined && separator}

              {index !== array.length - 1 && gap !== undefined && (
                <Box
                  accessibilityElementsHidden
                  accessibilityLiveRegion="none"
                  accessible={false}
                  importantForAccessibility="no"
                  {...{ [sizeProperty]: resolvedGap }}
                />
              )}
            </Fragment>
          ))}
    </BaseBox>
  );
}

export function Column(props: Omit<BoxProps, 'flexDirection'>) {
  return <Box {...props} flexDirection="column" />;
}

function AnimatedColumnNative(props: ComponentProps<typeof Column>) {
  return <Column {...props} />;
}

// Give both platform branches one props contract instead of expanding their union in JSX.
const AnimatedColumnComponent: ComponentType<ComponentProps<typeof Column>> = !isMobileNative
  ? Column
  : Animated.createAnimatedComponent(AnimatedColumnNative);

export function AnimatedColumn(props: ComponentProps<typeof Column>) {
  return <AnimatedColumnComponent {...props} />;
}

export function Row(props: Omit<BoxProps, 'flexDirection'> & { reverse?: boolean }) {
  const flexDirection = props.reverse ? 'row-reverse' : 'row';
  return <Box {...props} flexDirection={flexDirection} />;
}

function AnimatedRowNative(props: ComponentProps<typeof Row>) {
  return <Row {...props} />;
}

const AnimatedRowComponent: ComponentType<ComponentProps<typeof Row>> = !isMobileNative
  ? Row
  : Animated.createAnimatedComponent(AnimatedRowNative);

export function AnimatedRow(props: ComponentProps<typeof Row>) {
  return <AnimatedRowComponent {...props} />;
}
